import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

let pluginsRegistered = false;

// On touch devices the address bar show/hide fires height-only resizes that
// must be ignored (they caused visible jumps). On desktop a height change
// (window resize, DevTools) is real and requires a ScrollTrigger refresh.
export const isTouchDevice = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(hover: none), (pointer: coarse)").matches;

export const registerGsapPlugins = () => {
  if (pluginsRegistered || typeof window === "undefined") return;
  gsap.registerPlugin(ScrollTrigger);
  // "resize" is intentionally excluded: mobile address-bar show/hide fires
  // resize and caused visible jumps. Components handle width-only resizes
  // themselves.
  ScrollTrigger.config({
    ignoreMobileResize: true,
    autoRefreshEvents: "visibilitychange,DOMContentLoaded,load",
  });
  pluginsRegistered = true;
};

// Late-loading media (especially video metadata, which arrives after the
// window "load" event) changes the document height AFTER ScrollTriggers have
// measured their start/end positions. That makes pinned sections engage at
// the wrong scroll position and visually "jump". This watcher refreshes
// ScrollTrigger (debounced) whenever an <img> or <video> finishes loading
// and the document height actually changed.
let watcherCount = 0;
let removeWatcher = null;

export const watchMediaLoadRefresh = () => {
  if (typeof window === "undefined") return () => {};
  watcherCount += 1;

  if (watcherCount === 1) {
    let timeout = null;
    let lastHeight = document.body.scrollHeight;

    const scheduleRefresh = () => {
      if (document.body.scrollHeight === lastHeight) return;
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        ScrollTrigger.refresh();
        // Record height after refresh: pin spacers may change it and we
        // don't want that to schedule another refresh.
        lastHeight = document.body.scrollHeight;
      }, 200);
    };

    // "load" (img) and "loadedmetadata" (video) don't bubble, so listen in
    // the capture phase on window.
    const onMediaEvent = (event) => {
      const tag = event.target?.tagName;
      if (tag === "IMG" || tag === "VIDEO") scheduleRefresh();
    };

    window.addEventListener("load", onMediaEvent, true);
    window.addEventListener("loadedmetadata", onMediaEvent, true);
    if (document.fonts?.ready) {
      document.fonts.ready.then(scheduleRefresh).catch(() => {});
    }

    removeWatcher = () => {
      clearTimeout(timeout);
      window.removeEventListener("load", onMediaEvent, true);
      window.removeEventListener("loadedmetadata", onMediaEvent, true);
    };
  }

  return () => {
    watcherCount -= 1;
    if (watcherCount === 0 && removeWatcher) {
      removeWatcher();
      removeWatcher = null;
    }
  };
};
